import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Shield, CheckCircle2, XCircle, Loader2, Clock, AlertTriangle, RefreshCw, Mail,
} from 'lucide-react';
import { SumsubVerification } from '@/components/onboarding/SumsubVerification';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

/**
 * Public, anonymous KYC verification page for PM end users.
 *
 * URL:  /pm/verify?token=<kyc_link_token>
 *
 * Resolved states:
 *   - loading        — RPC in flight
 *   - missing        — no ?token= in URL
 *   - invalid        — token doesn't exist (typo, revoked, never issued)
 *   - expired        — token is real but past kyc_link_expires_at → offer self-renew
 *   - completed      — KYC already approved (idempotent landing)
 *   - sumsub         — happy path, mount the Sumsub WebSDK
 *   - sumsub_error   — Sumsub launch / token / network failure → offer retry / new link
 *   - done           — applicant just completed verification in this session
 */

type Stage =
  | 'loading'
  | 'missing'
  | 'invalid'
  | 'expired'
  | 'completed'
  | 'sumsub'
  | 'sumsub_error'
  | 'done';

interface ResolvedRow {
  end_user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  expires_at: string | null;
  is_completed: boolean;
  is_expired: boolean;
}

export default function PMVerifyEndUser() {
  const [params, setParams] = useSearchParams();
  const token = params.get('token') || '';

  const [stage, setStage] = useState<Stage>('loading');
  const [resolved, setResolved] = useState<ResolvedRow | null>(null);
  const [sumsubError, setSumsubError] = useState<string | null>(null);
  const [renewing, setRenewing] = useState(false);
  const [renewError, setRenewError] = useState<string | null>(null);

  const resolve = useCallback(async (t: string) => {
    setStage('loading');
    setRenewError(null);
    if (!t) {
      setStage('missing');
      return;
    }
    try {
      const { data, error } = await (supabase as any).rpc('resolve_pm_kyc_token', { _token: t });
      if (error) throw error;
      const row: ResolvedRow | null = Array.isArray(data) && data.length ? data[0] : null;
      if (!row) {
        setStage('invalid');
        return;
      }
      setResolved(row);
      if (row.is_completed) setStage('completed');
      else if (row.is_expired) setStage('expired');
      else setStage('sumsub');
    } catch (e: any) {
      // Treat hard RPC errors the same as invalid — never leak details to the public page.
      console.error('[pm-verify] resolve failed', e);
      setStage('invalid');
    }
  }, []);

  useEffect(() => {
    resolve(token);
  }, [token, resolve]);

  const handleRequestNewLink = async () => {
    setRenewing(true);
    setRenewError(null);
    try {
      const { data, error } = await (supabase as any).rpc('request_pm_kyc_link_renewal', { _token: token });
      if (error) throw error;
      const row = Array.isArray(data) && data.length ? data[0] : null;
      if (!row?.success) {
        const code = row?.error_code || 'unknown';
        const messages: Record<string, string> = {
          invalid_token: 'This verification link could not be found. Please contact your program manager for a new one.',
          already_approved: 'Your verification is already complete — no new link is needed.',
          rate_limited: 'A new link was just requested. Please wait a minute before trying again.',
          unknown: 'We could not generate a new link right now. Please try again or contact your program manager.',
        };
        setRenewError(messages[code] || messages.unknown);
        return;
      }
      // Swap the URL token in place and re-resolve.
      setParams({ token: row.new_token }, { replace: true });
    } catch (e: any) {
      console.error('[pm-verify] renewal failed', e);
      setRenewError('We could not generate a new link right now. Please try again in a moment.');
    } finally {
      setRenewing(false);
    }
  };

  const handleSumsubRetry = () => {
    setSumsubError(null);
    setStage('loading');
    // brief tick so the Sumsub component fully unmounts before remount
    setTimeout(() => setStage('sumsub'), 50);
  };

  // ─── Shared shells ────────────────────────────────────────────────────────
  const Shell: React.FC<React.PropsWithChildren> = ({ children }) => (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 text-center">
      <div className="max-w-md w-full">{children}</div>
    </div>
  );

  // ─── State: loading ───────────────────────────────────────────────────────
  if (stage === 'loading') {
    return (
      <Shell>
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-3 mx-auto" />
        <p className="text-sm text-muted-foreground">Loading verification…</p>
      </Shell>
    );
  }

  // ─── State: missing token ─────────────────────────────────────────────────
  if (stage === 'missing') {
    return (
      <Shell>
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4 mx-auto">
          <AlertTriangle className="w-8 h-8 text-muted-foreground" />
        </div>
        <h1 className="text-xl font-bold text-foreground mb-2">No verification link</h1>
        <p className="text-muted-foreground text-sm">
          Please open the verification link your program manager shared with you. It looks like
          <code className="mx-1 px-1.5 py-0.5 rounded bg-muted text-xs">/pm/verify?token=…</code>
        </p>
      </Shell>
    );
  }

  // ─── State: invalid token ─────────────────────────────────────────────────
  if (stage === 'invalid') {
    return (
      <Shell>
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4 mx-auto">
          <XCircle className="w-8 h-8 text-destructive" />
        </div>
        <h1 className="text-xl font-bold text-destructive mb-2">Link not recognised</h1>
        <p className="text-muted-foreground text-sm mb-6">
          This verification link is not valid. It may have been mistyped or already revoked by your program manager.
          Please contact them and ask for a new link.
        </p>
        <Button
          variant="outline"
          onClick={() => window.location.assign('mailto:?subject=New%20verification%20link%20needed')}
          className="gap-2"
        >
          <Mail className="w-4 h-4" /> Contact program manager
        </Button>
      </Shell>
    );
  }

  // ─── State: expired token (with self-service renewal) ─────────────────────
  if (stage === 'expired' && resolved) {
    return (
      <Shell>
        <div className="w-16 h-16 rounded-full bg-warning/10 flex items-center justify-center mb-4 mx-auto">
          <Clock className="w-8 h-8 text-warning" />
        </div>
        <h1 className="text-xl font-bold text-foreground mb-2">This verification link has expired</h1>
        <p className="text-muted-foreground text-sm mb-6">
          {resolved.first_name
            ? `Hi ${resolved.first_name}, your previous link is no longer valid.`
            : 'Your previous verification link is no longer valid.'}{' '}
          You can request a new one below — it stays valid for 30 days.
        </p>
        <Button onClick={handleRequestNewLink} disabled={renewing} className="gap-2 w-full sm:w-auto">
          {renewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {renewing ? 'Generating…' : 'Request a new verification link'}
        </Button>
        {renewError && (
          <p className="mt-4 text-sm text-destructive flex items-start gap-2 justify-center">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{renewError}</span>
          </p>
        )}
      </Shell>
    );
  }

  // ─── State: already completed ─────────────────────────────────────────────
  if (stage === 'completed' && resolved) {
    return (
      <Shell>
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4 mx-auto">
          <CheckCircle2 className="w-8 h-8 text-success" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">Verification complete</h1>
        <p className="text-muted-foreground text-sm">
          {resolved.first_name ? `Thanks, ${resolved.first_name}. ` : 'Thank you. '}
          Your identity has been verified. Your program manager has been notified — no further action is needed.
        </p>
      </Shell>
    );
  }

  // ─── State: just-finished (this session) ──────────────────────────────────
  if (stage === 'done' && resolved) {
    return (
      <Shell>
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4 mx-auto">
          <CheckCircle2 className="w-8 h-8 text-success" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">Verification submitted</h1>
        <p className="text-muted-foreground text-sm">
          {resolved.first_name ? `Thank you, ${resolved.first_name}. ` : 'Thank you. '}
          Your identity has been submitted. Your program manager will be notified once the review is finished —
          you can safely close this window.
        </p>
      </Shell>
    );
  }

  // ─── State: Sumsub launch / runtime error ─────────────────────────────────
  if (stage === 'sumsub_error' && resolved) {
    return (
      <Shell>
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4 mx-auto">
          <AlertTriangle className="w-8 h-8 text-destructive" />
        </div>
        <h1 className="text-xl font-bold text-foreground mb-2">Verification could not start</h1>
        <p className="text-muted-foreground text-sm mb-6">
          We hit a problem launching the identity check
          {sumsubError ? (
            <>
              :{' '}
              <span className="text-foreground/80">{sumsubError}</span>
            </>
          ) : (
            '.'
          )}{' '}
          You can try again, or request a fresh verification link.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button onClick={handleSumsubRetry} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" /> Try again
          </Button>
          <Button onClick={handleRequestNewLink} disabled={renewing} className="gap-2">
            {renewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Request a new link
          </Button>
        </div>
        {renewError && (
          <p className="mt-4 text-sm text-destructive flex items-start gap-2 justify-center">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{renewError}</span>
          </p>
        )}
      </Shell>
    );
  }

  // ─── Happy path: mount Sumsub ─────────────────────────────────────────────
  if (stage === 'sumsub' && resolved) {
    // Per-launch suffix so each retry spawns a fresh applicant on Sumsub side.
    const sumsubPersonId = `pm-eu-${resolved.end_user_id}-${Date.now()}`;
    const fullName =
      resolved.first_name || resolved.last_name
        ? `${resolved.first_name || ''} ${resolved.last_name || ''}`.trim()
        : undefined;

    return (
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Identity Verification</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {fullName
                ? `Hello ${fullName}, please verify your identity to receive your card.`
                : 'Please complete identity verification to receive your card.'}
            </p>
          </div>

          <SumsubVerification
            personId={sumsubPersonId}
            email={resolved.email || undefined}
            personName={fullName}
            includePOA={false}
            onComplete={() => setStage('done')}
            onError={(e) => {
              setSumsubError(typeof e === 'string' ? e : 'Unexpected error');
              setStage('sumsub_error');
            }}
          />
        </div>
      </div>
    );
  }

  // Fallback (should never hit)
  return (
    <Shell>
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto" />
    </Shell>
  );
}
